// GoalTimelineCard (plan todo 23) — renders getGoalTimeline as weeks + goal
// date RANGE (never a guaranteed date), and reuses the same card style for
// getExpenditureInfo (value, kind badge, confidence) per the todo contract.

import { CalendarRange, Flame } from "lucide-react";
import { formatDate, formatNumber } from "../../lib/format";
import type { ExpenditureData, GoalTimelineData } from "./card-data";
import { ChatCardShell, MacroChip, OpenInButton } from "./card-ui";

export interface GoalTimelineCardProps {
  readonly data: GoalTimelineData;
  readonly onSwitchScreen?: (screen: string) => void;
}

function formatIsoDate(iso: string): string | undefined {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : formatDate(ms);
}

export function GoalTimelineCard({ data, onSwitchScreen }: GoalTimelineCardProps) {
  const dateMin = data.goalDateMin !== undefined ? formatIsoDate(data.goalDateMin) : undefined;
  const dateMax = data.goalDateMax !== undefined ? formatIsoDate(data.goalDateMax) : undefined;
  return (
    <ChatCardShell
      icon={CalendarRange}
      title="Goal timeline"
      subtitle="Always a range — it updates with real data"
      testId="chat-card-goal-timeline"
    >
      <div className="flex flex-col gap-1.5 text-sm">
        {(data.weeksMin !== undefined || data.weeksMax !== undefined || data.weeksToGoal !== undefined) && (
          <div className="flex items-baseline justify-between" data-testid="goal-timeline-weeks">
            <span className="text-text-muted">Estimated time to goal</span>
            <span className="font-semibold text-text">
              {data.weeksMin !== undefined && data.weeksMax !== undefined
                ? `${formatNumber(data.weeksMin)}–${formatNumber(data.weeksMax)} weeks`
                : data.weeksToGoal !== undefined
                  ? `About ${formatNumber(data.weeksToGoal)} weeks`
                  : "—"}
            </span>
          </div>
        )}
        {(dateMin !== undefined || dateMax !== undefined) && (
          <div className="flex items-baseline justify-between" data-testid="goal-timeline-dates">
            <span className="text-text-muted">Goal date range</span>
            <span className="font-medium text-text">
              {dateMin ?? "?"} – {dateMax ?? "?"}
            </span>
          </div>
        )}
        {data.weeklyChangeKg !== undefined && (
          <div className="flex items-baseline justify-between" data-testid="goal-timeline-rate">
            <span className="text-text-muted">Planned weekly change</span>
            <span className="font-medium text-text">{data.weeklyChangeKg.toFixed(2)} kg</span>
          </div>
        )}
        {data.targetKcal !== undefined && (
          <div className="flex items-baseline justify-between" data-testid="goal-timeline-target">
            <span className="text-text-muted">Daily target</span>
            <span className="font-medium text-text">{formatNumber(data.targetKcal)} kcal</span>
          </div>
        )}
      </div>
      {data.restricted === true && (
        <p className="rounded-xl bg-warning/10 px-3 py-2 text-xs leading-relaxed text-warning" data-testid="goal-timeline-restricted">
          Automated projection is restricted for safety; these dates are not an active weight-change plan.
        </p>
      )}
      <OpenInButton
        label="Open in Plan"
        screen="plan"
        testId="goal-timeline-open-btn"
        onSwitchScreen={onSwitchScreen}
      />
    </ChatCardShell>
  );
}

export interface ExpenditureCardProps {
  readonly data: ExpenditureData;
  readonly onSwitchScreen?: (screen: string) => void;
}

export function ExpenditureInfoCard({ data, onSwitchScreen }: ExpenditureCardProps) {
  return (
    <ChatCardShell
      icon={Flame}
      title="Daily expenditure"
      testId="chat-card-expenditure"
    >
      {data.expenditureKcal !== undefined && (
        <div className="rounded-xl bg-accent-light px-3 py-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-accent">
            Estimated calories used per day
          </div>
          <div className="text-lg font-bold leading-tight text-accent" data-testid="expenditure-kcal">
            {formatNumber(data.expenditureKcal)} kcal
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {data.kind !== undefined && <MacroChip label="Type" value={data.kind} />}
        {data.confidence !== undefined && <MacroChip label="Confidence" value={data.confidence} />}
      </div>
      {data.explanation !== undefined && (
        <p className="text-xs leading-relaxed text-text-muted" data-testid="expenditure-explanation">
          {data.explanation}
        </p>
      )}
      <OpenInButton
        label="Open in Plan"
        screen="plan"
        testId="expenditure-open-btn"
        onSwitchScreen={onSwitchScreen}
      />
    </ChatCardShell>
  );
}
